import { redirect } from '@tanstack/react-router';
import { queryOptions, useQuery, type QueryClient } from '@tanstack/react-query';
import { api, ApiError } from '@/api/client';

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  emailConfirmed: boolean;
  roles: string[];
}

/**
 * Fetches the current account profile from `/api/v1/account/profile`.
 * Returns `null` on 401 (not authenticated) instead of throwing so callers
 * can treat anonymous and authenticated cases uniformly.
 */
async function fetchAuthUser(): Promise<AuthUser | null> {
  try {
    return await api<AuthUser>('/account/profile');
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) return null;
    throw err;
  }
}

export const authQueryOptions = queryOptions({
  queryKey: ['auth', 'me'] as const,
  queryFn: fetchAuthUser,
  staleTime: 5 * 60_000,
  retry: false,
});

export function useAuth() {
  return useQuery(authQueryOptions);
}

/** True when the signed-in user holds the given role. */
export function useHasRole(role: string): boolean {
  const { data } = useAuth();
  return data?.roles?.includes(role) ?? false;
}

/** Convenience for the common Administrator check. */
export function useIsAdmin(): boolean {
  return useHasRole('Administrator');
}

/**
 * Router guard for protected routes. Use in `beforeLoad`:
 *   beforeLoad: ({ context, location }) => requireAuth({ context, location })
 *
 * Redirects to /login with a `redirectTo` search param so the user lands back
 * where they tried to go after signing in.
 */
export async function requireAuth({
  context,
  location,
}: {
  context: { queryClient: QueryClient };
  location: { href: string };
}): Promise<{ user: AuthUser }> {
  const user = await context.queryClient.fetchQuery(authQueryOptions);
  if (!user) {
    throw redirect({ to: '/login', search: { redirectTo: location.href } });
  }
  return { user };
}

/**
 * Router guard for role-restricted routes. Use in `beforeLoad`:
 *   beforeLoad: ({ context, location }) => requireRole('Administrator', { context, location })
 *
 * Anonymous users are sent to /login (with a return path); signed-in users who
 * lack the role are bounced to /returns rather than shown the admin shell.
 */
export async function requireRole(
  role: string,
  {
    context,
    location,
  }: {
    context: { queryClient: QueryClient };
    location: { href: string };
  },
): Promise<{ user: AuthUser }> {
  const user = await context.queryClient.fetchQuery(authQueryOptions);
  if (!user) {
    throw redirect({ to: '/login', search: { redirectTo: location.href } });
  }
  if (!user.roles?.includes(role)) {
    throw redirect({ to: '/returns' });
  }
  return { user };
}

/**
 * Inverse guard for login / register pages — if the user is already signed in,
 * bounce them to /returns (or the requested `redirectTo`).
 */
export async function requireAnonymous({
  context,
  redirectTo,
}: {
  context: { queryClient: QueryClient };
  redirectTo?: string;
}): Promise<void> {
  const user = await context.queryClient.fetchQuery(authQueryOptions);
  if (user) {
    throw redirect({ to: (redirectTo as '/returns') ?? '/returns' });
  }
}
